import { useCallback, useEffect, useState } from "react";
import { api, fmtUSD } from "@/lib/api";
import { SectionCard } from "./SectionCard";
import { toast } from "sonner";

const BADGE = {
  "SIN DECISIÓN": "bg-red-100 text-red-700 border-red-200",
  REVISAR: "bg-amber-100 text-amber-800 border-amber-200",
  "Con decisión": "bg-emerald-100 text-emerald-800 border-emerald-200",
};

export default function DeudasPlan({ fechaCorte, vendedor }) {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null); // cliente en edición
  const [f, setF] = useState({ decision: "", motivo: "", nota: "", proxima_revision: "" });

  const load = useCallback(async () => {
    try {
      const params = { fecha_corte: fechaCorte, vendedor, _t: Date.now() };
      const r = await api.get("/cartera/deudas-60", { params });
      setD(r.data);
    } catch (e) {
      toast.error("No se pudo cargar el plan de deudas +60 días");
    }
  }, [fechaCorte, vendedor]);
  useEffect(() => { load(); }, [load]);

  const abrir = (c) => {
    setEdit(c);
    setF({ decision: c.decision || "", motivo: c.motivo || "", nota: c.nota || "", proxima_revision: c.proxima_revision || "" });
  };
  const guardar = async () => {
    try {
      await api.put("/cartera/deudas-60/decision", {
        vendedor: edit.vendedor, cliente: edit.cliente,
        decision: f.decision || null, motivo: f.motivo || null, nota: f.nota, proxima_revision: f.proxima_revision || null,
      });
      toast.success("Decisión guardada");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };

  if (!d) return null;
  return (
    <SectionCard
      id="deudas-60"
      testid="section-deudas-60"
      num="•"
      title="Plan de acción · deudas de más de 60 días"
      description="Por cliente y tramo de atraso. La sugerencia es solo orientativa: la decisión la registra una persona. “Evaluar acción jurídica” deriva el caso al área legal."
      action={
        <span className="text-xs font-semibold text-slate-600">
          {d.sin_decision} sin decisión · {d.a_revisar} a revisar · {fmtUSD(d.total)}
        </span>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {d.tramos.map((t) => (
          <div key={t} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="text-xs text-slate-500">{t}</div>
            <div className="font-bold text-slate-900">{fmtUSD(d.por_tramo[t])}</div>
          </div>
        ))}
      </div>
      {d.filas_sin_antiguedad > 0 && (
        <p className="text-xs text-slate-500 mb-2">{d.filas_sin_antiguedad} filas sin fecha de vencimiento por factura no se incluyen.</p>
      )}
      <div className="overflow-x-auto rounded-lg border border-slate-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left px-3 py-2">Cliente</th>
              <th className="text-left px-3 py-2">Vendedor</th>
              {d.tramos.map((t) => <th key={t} className="text-right px-3 py-2">{t}</th>)}
              <th className="text-right px-3 py-2">Total</th>
              <th className="text-left px-3 py-2">Sugerencia</th>
              <th className="text-left px-3 py-2">Decisión</th>
              <th className="text-center px-3 py-2">Estado</th>
            </tr>
          </thead>
          <tbody>
            {d.clientes.map((c) => (
              <tr key={c.vendedor + c.cliente} className="border-t border-slate-100 hover:bg-emerald-50/40">
                <td className="px-3 py-2 font-medium">{c.cliente}</td>
                <td className="px-3 py-2">{c.vendedor}</td>
                {d.tramos.map((t) => (
                  <td key={t} className="px-3 py-2 text-right tabular-nums">{c.tramos[t] ? fmtUSD(c.tramos[t]) : "—"}</td>
                ))}
                <td className="px-3 py-2 text-right font-bold tabular-nums">{fmtUSD(c.total)}</td>
                <td className="px-3 py-2 text-slate-500">{c.sugerencia}</td>
                <td className="px-3 py-2">
                  {c.decision || "—"}
                  {c.motivo ? <div className="text-xs text-slate-500">motivo: {c.motivo}</div> : null}
                  {c.proxima_revision ? <div className="text-xs text-slate-400">revisar {c.proxima_revision}</div> : null}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-2 py-0.5 text-xs font-bold border rounded-full ${BADGE[c.estado]}`}>{c.estado}</span>
                  {d.puede_decidir && (
                    <button onClick={() => abrir(c)} className="block mx-auto mt-1 text-xs text-emerald-700 underline">Decidir</button>
                  )}
                </td>
              </tr>
            ))}
            {d.clientes.length === 0 && (
              <tr><td colSpan={d.tramos.length + 6} className="px-3 py-6 text-center text-slate-500">No hay deudas de más de 60 días.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-bold">{edit.cliente}</h4>
            <p className="text-xs text-slate-500">{edit.vendedor} · {fmtUSD(edit.total)} · sugerencia: {edit.sugerencia}</p>
            <select value={f.decision} onChange={(e) => setF({ ...f, decision: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Sin decisión</option>
              {d.decisiones.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <label className="block text-xs text-slate-500">¿Por qué se atrasó? <span className="text-red-600">*</span>
              <select value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="deuda-motivo">
                <option value="">Elegí el motivo…</option>
                {(d.motivos || []).map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
              <span className="text-[11px] text-slate-400">Clima y logística no perjudican al cliente en el análisis; la espera de mejor precio sí.</span>
            </label>
            <textarea value={f.nota} maxLength={500} onChange={(e) => setF({ ...f, nota: e.target.value })} placeholder="Nota (opcional)" className="w-full border rounded-lg px-3 py-2 text-sm" rows={3} />
            <label className="block text-xs text-slate-500">Próxima revisión
              <input type="date" value={f.proxima_revision} onChange={(e) => setF({ ...f, proxima_revision: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEdit(null)} className="px-3 py-2 text-sm rounded-lg border">Cancelar</button>
              <button onClick={guardar} disabled={!!f.decision && !f.motivo} title={f.decision && !f.motivo ? "Elegí el motivo del atraso" : ""} className="px-3 py-2 text-sm rounded-lg bg-emerald-700 text-white disabled:opacity-50">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
